import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, Download, RefreshCw, RotateCcw } from 'lucide-react'
import { models, Report } from 'powerbi-client'
import { PowerBIEmbed } from 'powerbi-client-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  exportPowerBiReport,
  type ExportFormat,
  type PowerBiDashboardType,
  type PowerBiEmbedConfig,
} from '@/api/powerbi'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

export interface PowerBiSlicerConfig {
  title: string
  target: models.IFilterTarget
  values: (string | number)[]
}

function getEmbedErrorMessage(event?: { detail?: { message?: string } }) {
  const message = event?.detail?.message

  if (typeof message === 'string' && message.trim()) {
    return message
  }

  return 'Failed to render the Power BI dashboard'
}

interface PowerBiReportEmbedProps {
  embedConfig: PowerBiEmbedConfig
  reportType: PowerBiDashboardType
  pageName?: string
  filters?: models.IFilter[]
  slicer?: PowerBiSlicerConfig
  showPageNavigation?: boolean
  loadingLabel?: string
  cssClassName?: string
  onRefresh?: () => void
  isFetching?: boolean
}

export function PowerBiReportEmbed({
  embedConfig,
  reportType,
  pageName,
  filters,
  slicer,
  showPageNavigation = true,
  loadingLabel = 'dashboard',
  cssClassName = 'h-180 w-full',
  onRefresh,
  isFetching = false,
}: PowerBiReportEmbedProps) {
  const [isReportRendered, setIsReportRendered] = useState(false)
  const [embedError, setEmbedError] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const lastToastMessageRef = useRef<string | null>(null)
  const slicerRef = useRef<PowerBiSlicerConfig | undefined>(slicer)
  const reportRef = useRef<Report | null>(null)

  useEffect(() => {
    slicerRef.current = slicer
  }, [slicer])

  useEffect(() => {
    setIsReportRendered(false)
    setEmbedError(null)
  }, [embedConfig.accessToken, embedConfig.embedUrl, embedConfig.reportId])

  const eventHandlers = useMemo(
    () =>
      new Map<
        string,
        (event: unknown, embeddedEntity: unknown) => void | Promise<void>
      >([
        [
          'loaded',
          async () => {
            setEmbedError(null)
            const report = reportRef.current
            const target = slicerRef.current
            const debugEnabled = import.meta.env.VITE_PBI_DEBUG === 'true'
            if (debugEnabled && report) {
              ;(window as unknown as { pbi?: Report }).pbi = report

              console.group('[PBI debug]')
              console.log('report exposed as window.pbi', report)
              try {
                const pages = await report.getPages()
                const active = pages.find(p => p.isActive) ?? pages[0]
                console.log('active page', active?.name, active?.displayName)
                if (active) {
                  const visuals = await active.getVisuals()
                  console.table(
                    visuals.map(v => ({
                      name: v.name,
                      type: v.type,
                      title: v.title,
                    }))
                  )
                  for (const v of visuals) {
                    if (v.type === 'slicer') {
                      try {
                        const state = await v.getSlicerState()
                        console.log(
                          'slicer',
                          v.title || v.name,
                          'state:',
                          state
                        )
                      } catch (e) {
                        console.warn('getSlicerState failed for', v.name, e)
                      }
                    }
                  }
                }
                console.log('report filters', await report.getFilters())
              } catch (e) {
                console.warn('debug introspection failed', e)
              }
              console.groupEnd()
            }
            if (!report || !target) return
            try {
              const pages = await report.getPages()
              const activePage = pages.find(p => p.isActive) ?? pages[0]
              if (!activePage) return
              const visuals = await activePage.getVisuals()
              const debugEnabled = import.meta.env.VITE_PBI_DEBUG === 'true'
              if (debugEnabled) {
                ;(window as unknown as { pbi?: Report }).pbi = report
              }

              const targetColumn = target.target as {
                table?: string
                column?: string
              }
              if (debugEnabled) {
                console.group('[PBI slicer debug]')
                console.log(
                  'looking for slicer bound to:',
                  `${targetColumn.table}.${targetColumn.column}`
                )
                console.table(
                  visuals.map(v => ({
                    name: v.name,
                    type: v.type,
                    title: v.title,
                  }))
                )
              }
              const slicers = visuals.filter(v => v.type === 'slicer')
              let slicerVisual: (typeof slicers)[number] | undefined
              for (const s of slicers) {
                try {
                  const state = await s.getSlicerState()
                  const targets = (
                    state as {
                      targets?: { table?: string; column?: string }[]
                    }
                  ).targets
                  if (debugEnabled) {
                    console.log(
                      'slicer',
                      s.name,
                      'targets:',
                      JSON.stringify(targets)
                    )
                  }
                  if (
                    targets?.some(
                      t =>
                        t.table === targetColumn.table &&
                        t.column === targetColumn.column
                    )
                  ) {
                    slicerVisual = s
                    break
                  }
                } catch (e) {
                  if (debugEnabled) {
                    console.warn('getSlicerState failed for', s.name, e)
                  }
                }
              }
              if (debugEnabled) {
                console.groupEnd()
              }

              if (!slicerVisual) {
                toast.error(
                  `Slicer for ${targetColumn.table}.${targetColumn.column} not found`
                )
                return
              }
              const basicFilter: models.IBasicFilter = {
                $schema: 'http://powerbi.com/product/schema#basic',
                target: target.target,
                operator: 'In',
                values: target.values,
                filterType: models.FilterType.Basic,
              }
              await slicerVisual.setSlicerState({ filters: [basicFilter] })
            } catch (err) {
              const message =
                err instanceof Error ? err.message : 'Failed to set slicer'
              toast.error(message)
            }
          },
        ],
        [
          'rendered',
          () => {
            setEmbedError(null)
            setIsReportRendered(true)
          },
        ],
        [
          'error',
          event => {
            const message = getEmbedErrorMessage(
              event as { detail?: { message?: string } } | undefined
            )

            setEmbedError(message)
            setIsReportRendered(false)

            if (lastToastMessageRef.current !== message) {
              lastToastMessageRef.current = message
              toast.error(message)
            }
          },
        ],
      ]),
    []
  )

  const handleReload = () => {
    lastToastMessageRef.current = null
    setEmbedError(null)
    setIsReportRendered(false)
    onRefresh?.()
  }

  const handleExport = useCallback(
    async (format: ExportFormat, scope: 'currentPage' | 'wholeReport') => {
      const report = reportRef.current
      if (!report) return
      setIsExporting(true)
      try {
        let bookmarkState: string | undefined
        try {
          const bookmark = await report.bookmarksManager.capture({
            allPages: true,
          })
          bookmarkState = bookmark.state
        } catch {
          // capture can fail mid-render; export without bookmark state
        }
        let currentPageName: string | undefined
        if (scope === 'currentPage') {
          try {
            const activePage = await report.getActivePage()
            currentPageName = activePage?.name
          } catch {
            // fall back to whole-report export if active page lookup fails
          }
        }
        const result = await exportPowerBiReport(
          embedConfig.reportId,
          reportType,
          format,
          bookmarkState,
          currentPageName
        )
        const url = window.URL.createObjectURL(result.blob)
        const link = document.createElement('a')
        link.href = url
        link.download = result.filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        toast.error(getErrorMessage(err, 'Failed to export the report'))
      } finally {
        setIsExporting(false)
      }
    },
    [embedConfig.reportId, reportType]
  )

  const handleReset = useCallback(async () => {
    const report = reportRef.current
    if (!report) return

    setIsReportRendered(false)
    try {
      await report.reload()
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to reset the report'))
      setIsReportRendered(true)
    }
  }, [])

  return (
    <div className="flex min-h-180 flex-col bg-muted/20">
      <div className="flex items-center justify-end gap-1 px-2 py-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 cursor-pointer"
              disabled={!isReportRendered || isFetching || isExporting}
              aria-label="Export report"
            >
              {isExporting ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {(
              [
                { format: 'PDF', label: 'Export as PDF' },
                { format: 'PPTX', label: 'Export as PowerPoint' },
              ] as const
            ).map(({ format, label }) => (
              <DropdownMenuSub key={format}>
                <DropdownMenuSubTrigger className="cursor-pointer">
                  {label}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => void handleExport(format, 'currentPage')}
                    >
                      Current page
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => void handleExport(format, 'wholeReport')}
                    >
                      Whole report
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 cursor-pointer"
          onClick={() => void handleReset()}
          disabled={!isReportRendered || isFetching || isExporting}
          aria-label="Reset filters, slicers, and other data view changes"
          title="Reset filters, slicers, and other data view changes"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      </div>

      <div className="relative flex-1">
        {(!isReportRendered || isFetching) && !embedError && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3 text-center">
              <RefreshCw className="h-5 w-5 animate-spin text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Loading {loadingLabel}
                </p>
                <p className="text-xs text-muted-foreground">
                  This can take a few seconds while the report renders.
                </p>
              </div>
            </div>
          </div>
        )}

        {embedError && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/90 px-6 text-center backdrop-blur-sm">
            <div className="space-y-4">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-foreground">
                  Dashboard render failed
                </h2>
                <p className="max-w-xl text-sm text-muted-foreground">
                  {embedError}
                </p>
              </div>
              <div className="flex justify-center">
                <Button
                  type="button"
                  onClick={handleReload}
                  disabled={isFetching}
                >
                  Reload report
                </Button>
              </div>
            </div>
          </div>
        )}

        <PowerBIEmbed
          embedConfig={{
            type: 'report',
            id: embedConfig.reportId,
            embedUrl: embedConfig.embedUrl,
            accessToken: embedConfig.accessToken,
            tokenType: models.TokenType.Embed,
            pageName,
            filters: filters ?? [],
            settings: {
              background: models.BackgroundType.Transparent,
              panes: {
                filters: {
                  expanded: false,
                  visible: false,
                },
                pageNavigation: {
                  visible: showPageNavigation,
                },
              },
            },
          }}
          eventHandlers={eventHandlers}
          cssClassName={cssClassName}
          getEmbeddedComponent={embeddedReport => {
            reportRef.current = embeddedReport as Report
          }}
        />
      </div>
    </div>
  )
}
